import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { Company, CompanySite } from '../model';
import { CompanyService } from './service/company.service';
import { AppAuthService } from './auth';
import { filter, distinctUntilChanged, shareReplay, take } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UserService } from './service/user.service';
import { CompanyWriteAccess } from './service/company-write-access.service';

export enum AppMode {
  DEFAULT,
  EUDR_RISK,
  EUDR_TRACES,
  PPWR,
  WIDE,
  EXTRA_WIDE,
  HELP,
  FORM,
}

@Injectable({ providedIn: 'root' })
export class AppState {
  private companyService = inject(CompanyService);
  private userService = inject(UserService);
  private writeAccess = inject(CompanyWriteAccess);

  companySite$ = new BehaviorSubject<{ company: Company; site?: CompanySite } | undefined>(undefined);
  // Replays the latest list: top-nav and side-nav read it instead of fetching api/company/all
  // themselves, and must not miss a load that finished before they subscribed.
  companies$ = new ReplaySubject<Company[]>(1);
  mode$ = new BehaviorSubject<AppMode>(AppMode.DEFAULT);
  openSideNav$ = new BehaviorSubject<boolean>(true);
  readonly isLoggedIn$ = new BehaviorSubject<boolean>(false);
  readonly isLoggedInOnce$ = this.isLoggedIn$.pipe(filter(Boolean), take(1), shareReplay(1));

  constructor() {
    const authService = inject(AppAuthService);
    const route = inject(ActivatedRoute);
    const router = inject(Router);

    router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const currentUrl = router.url.split('?')[0];
      if (currentUrl.includes('/traces-eudr')) {
        this.mode$.next(AppMode.EUDR_TRACES);
        return;
      }

      const { mode } = AppState.firstRoute(route).snapshot.data;
      this.mode$.next(mode || AppMode.DEFAULT);
    });
    authService.isLoggedIn$.subscribe(async (loggedIn) => {
      this.isLoggedIn$.next(loggedIn);
      if (loggedIn) {
        await this.refresh();
      } else {
        this.setCompanies([]);
        this.selectCompanySite(undefined, undefined);
      }
    });
  }

  private static firstRoute = (route: ActivatedRoute): ActivatedRoute =>
    route.firstChild ? AppState.firstRoute(route.firstChild) : route;
  setCompanies = (companies: Company[]) => this.companies$.next(companies);
  selectCompanySite = (company: Company | undefined, site: CompanySite | undefined) => {
    this.companySite$.next(company ? { company, site } : undefined);
    // The one place the server's write rule enters the UI — see CompanyWriteAccess.
    this.writeAccess.select(company);
    // Save last selected company/site to user profile (fire and forget)
    // Skip saving for demo companies as they are read-only and should not be persisted
    if (site && !company?.demoData) {
      this.userService.setLastSelectedCompany(site.id).catch((error) => {
        console.error('Failed to save last selected company:', error);
      });
    }
  };
  refresh = async () => this.setCompanies(await this.companyService.listAll());
}
